// @ts-nocheck
import { useState } from 'react';
import { Avatar, AvatarGroup, Badge, Button, Card, Code, Group, Progress, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { mockProjects, mockUsers, getUserById, getUsersByIds } from '../mockData';

export default function ProjectsPage() {
  const [selectedProject, setSelectedProject] = useState(mockProjects[0]);

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Projects Domain Example</Title>
        <Text c="dimmed" size="sm">
          Real-world example of a project management system using components with relational mock data
        </Text>
      </Stack>

      {/* Overview */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Projects Overview</Title>
          <Text size="sm" c="dimmed">
            This example demonstrates a complete project management interface using Cards, Avatars, Badges, Progress bars, and relational data from our mock data system.
          </Text>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Features:</Text>
            <Text size="xs" c="dimmed" component="div">
              • 20 projects with relational links to users (owners and members)
              <br />
              • Status tracking (planning, in-progress, on-hold, completed, cancelled)
              <br />
              • Priority levels and progress indicators
              <br />
              • Budget tracking and categorization
              <br />
              • Real data relationships using IDs (projectId → userId)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Project Grid */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>All Projects ({mockProjects.length})</Title>
            <Button variant="gradient" gradientVariant="primary" size="sm">
              + New Project
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {mockProjects.slice(0, 9).map((project) => {
              const owner = getUserById(project.ownerId);
              const members = getUsersByIds(project.memberIds);

              return (
                <Card
                  key={project.id}
                  variant="elevated"
                  p="md"
                  hoverEffect="lift"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedProject(project)}
                >
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Badge
                        size="sm"
                        color={
                          project.status === 'completed'
                            ? 'green'
                            : project.status === 'in-progress'
                            ? 'blue'
                            : project.status === 'on-hold'
                            ? 'orange'
                            : 'gray'
                        }
                      >
                        {project.status}
                      </Badge>
                      <Badge
                        size="sm"
                        variant="outline"
                        color={
                          project.priority === 'urgent'
                            ? 'red'
                            : project.priority === 'high'
                            ? 'orange'
                            : project.priority === 'medium'
                            ? 'blue'
                            : 'gray'
                        }
                      >
                        {project.priority}
                      </Badge>
                    </Group>

                    <Stack gap="xs">
                      <Text fw={600} lineClamp={1}>
                        {project.name}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {project.description}
                      </Text>
                    </Stack>

                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">
                          Progress
                        </Text>
                        <Text size="xs" fw={600}>
                          {project.progress}%
                        </Text>
                      </Group>
                      <Progress value={project.progress} size="sm" />
                    </Stack>

                    <Group justify="space-between">
                      <Stack gap={4}>
                        <Text size="xs" c="dimmed">
                          Owner
                        </Text>
                        {owner && (
                          <Group gap="xs">
                            <Avatar src={owner.avatar} size="xs" />
                            <Text size="xs">{owner.name}</Text>
                          </Group>
                        )}
                      </Stack>

                      <Stack gap={4} align="flex-end">
                        <Text size="xs" c="dimmed">
                          Team ({members.length})
                        </Text>
                        <AvatarGroup
                          avatars={members.map((member) => ({
                            src: member.avatar,
                            name: member.name,
                          }))}
                          max={3}
                          size="xs"
                        />
                      </Stack>
                    </Group>

                    {project.budget && (
                      <Text size="xs" c="dimmed">
                        Budget: ${project.budget.toLocaleString()}
                      </Text>
                    )}
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        </Stack>
      </Card>

      {/* Selected Project Detail */}
      {selectedProject && (
        <Card withBorder p="lg">
          <Stack gap="md">
            <Title order={4}>Selected Project Details</Title>

            <Card variant="gradient" gradientVariant="primary" p="lg">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs">
                    <Text c="white" fw={700} size="xl">
                      {selectedProject.name}
                    </Text>
                    <Text c="white" opacity={0.9} size="sm">
                      {selectedProject.description}
                    </Text>
                  </Stack>
                  <Badge size="lg" color="white" variant="filled">
                    {selectedProject.progress}% Complete
                  </Badge>
                </Group>

                <Group gap="lg">
                  <Stack gap={4}>
                    <Text c="white" opacity={0.7} size="xs">
                      Status
                    </Text>
                    <Text c="white" fw={600} tt="capitalize">
                      {selectedProject.status}
                    </Text>
                  </Stack>
                  <Stack gap={4}>
                    <Text c="white" opacity={0.7} size="xs">
                      Priority
                    </Text>
                    <Text c="white" fw={600} tt="capitalize">
                      {selectedProject.priority}
                    </Text>
                  </Stack>
                  <Stack gap={4}>
                    <Text c="white" opacity={0.7} size="xs">
                      Category
                    </Text>
                    <Text c="white" fw={600}>
                      {selectedProject.category}
                    </Text>
                  </Stack>
                  {selectedProject.budget && (
                    <Stack gap={4}>
                      <Text c="white" opacity={0.7} size="xs">
                        Budget
                      </Text>
                      <Text c="white" fw={600}>
                        ${selectedProject.budget.toLocaleString()}
                      </Text>
                    </Stack>
                  )}
                </Group>
              </Stack>
            </Card>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Card variant="subtle" p="md">
                <Stack gap="sm">
                  <Text fw={600}>Project Owner</Text>
                  {getUserById(selectedProject.ownerId) && (
                    <Group gap="sm">
                      <Avatar src={getUserById(selectedProject.ownerId)?.avatar} size="md" />
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {getUserById(selectedProject.ownerId)?.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {getUserById(selectedProject.ownerId)?.title}
                        </Text>
                      </Stack>
                    </Group>
                  )}
                </Stack>
              </Card>

              <Card variant="subtle" p="md">
                <Stack gap="sm">
                  <Text fw={600}>Team Members ({selectedProject.memberIds.length})</Text>
                  <Stack gap="xs">
                    {getUsersByIds(selectedProject.memberIds).slice(0, 3).map((member) => (
                      <Group key={member.id} gap="xs">
                        <Avatar src={member.avatar} size="sm" />
                        <Text size="sm">{member.name}</Text>
                      </Group>
                    ))}
                    {selectedProject.memberIds.length > 3 && (
                      <Text size="xs" c="dimmed">
                        +{selectedProject.memberIds.length - 3} more members
                      </Text>
                    )}
                  </Stack>
                </Stack>
              </Card>
            </SimpleGrid>

            <Card variant="outline" p="md">
              <Stack gap="xs">
                <Text fw={600}>Tags</Text>
                <Group gap="xs">
                  {selectedProject.tags.map((tag) => (
                    <Badge key={tag} variant="light">
                      {tag}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Card>
      )}

      {/* Code Example */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Implementation Code</Title>
          <Text size="sm" c="dimmed">
            This example shows how to use relational mock data with UI components
          </Text>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Data Structure:</Text>
            <Code block language="typescript">
{`// Import mock data and helpers
import { mockProjects, getUserById, getUsersByIds } from './mockData';

// Access project with relational data
const project = mockProjects[0];
const owner = getUserById(project.ownerId); // Get owner by ID
const members = getUsersByIds(project.memberIds); // Get all members`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Component Usage:</Text>
            <Code block language="tsx">
{`<Card variant="elevated" p="md">
  <Stack gap="sm">
    {/* Status and Priority */}
    <Group justify="space-between">
      <Badge color="blue">{project.status}</Badge>
      <Badge color="orange">{project.priority}</Badge>
    </Group>

    {/* Project Info */}
    <Text fw={600}>{project.name}</Text>
    <Text size="xs" c="dimmed">{project.description}</Text>

    {/* Progress */}
    <Progress value={project.progress} size="sm" />

    {/* Owner */}
    <Group gap="xs">
      <Avatar src={owner.avatar} size="xs" />
      <Text size="xs">{owner.name}</Text>
    </Group>

    {/* Team Members */}
    <AvatarGroup
      avatars={members.map((member) => ({
        src: member.avatar,
        name: member.name,
      }))}
      max={3}
      size="xs"
    />
  </Stack>
</Card>`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
